import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import { useStore, FEE_CONFIG, DAILY_CHARGE_LIMIT } from '../store/useStore'
import { useI18n } from '../i18n/useI18n'
import { formatHomeCurrency } from '../utils/currency'

const AMOUNTS = [3000, 5000, 10000, 20000, 30000, 50000]

export default function Charge() {
  const navigate = useNavigate()
  const { wallet, transactions } = useStore()
  const { t, locale } = useI18n()
  const [selected, setSelected] = useState<number | null>(null)

  const feeInfo = selected !== null ? FEE_CONFIG.charge.calculate(selected) : null

  // Daily charge total
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dailyChargeTotal = transactions
    .filter((tx) => tx.type === 'charge' && tx.status === 'completed' && new Date(tx.timestamp) >= today)
    .reduce((sum, tx) => sum + tx.amount, 0)
  const dailyRemaining = DAILY_CHARGE_LIMIT - dailyChargeTotal
  const exceedsLimit = selected !== null && selected + dailyChargeTotal > DAILY_CHARGE_LIMIT

  const handleContinue = () => {
    if (selected === null || !feeInfo || exceedsLimit) return
    // #1188 Phase 2: 直接 /charge/confirm に飛ばず、method picker を挟む。
    // fee/total は method 依存 (bank_transfer は 1.5%、card は 4.4%+350) なので
    // picker 側で recalc される。ここでは amount だけ渡せば十分。
    navigate('/charge/method', {
      state: { amount: selected, fee: feeInfo.fee, total: feeInfo.total },
    })
  }

  const balanceJPYC = wallet?.balanceJPYC ?? 0

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      <Header title={t('charge.title')} onBack={() => navigate(-1)} />

      {/* Current Balance */}
      <div className="flex flex-col items-center py-6">
        <span className="font-body text-sm text-text-secondary">
          {t('charge.currentBalance')}
        </span>
        <span className="mt-1 font-display text-[24px] font-bold text-gold">
          &yen; {balanceJPYC.toLocaleString()}
        </span>
      </div>

      {/* Divider */}
      <div className="mx-4 border-t border-gray-200" />

      {/* Select Amount Heading */}
      <h2 className="px-4 pt-5 pb-3 font-display text-base font-semibold text-text-primary">
        {t('charge.selectAmount')}
      </h2>

      {/* Amount Grid */}
      <div className="grid grid-cols-2 gap-3 px-4">
        {AMOUNTS.map((amount) => {
          const isSelected = selected === amount
          const homeEquiv = formatHomeCurrency(amount, locale)
          return (
            <button
              key={amount}
              onClick={() => setSelected(amount)}
              className={`tap-card flex h-20 flex-col items-center justify-center rounded-lg border bg-white transition-all ${
                isSelected
                  ? 'border-2 border-yokoso-red bg-red-50'
                  : 'border-gray-200'
              }`}
            >
              <span className="font-display text-xl font-bold text-text-primary">
                &yen;{amount.toLocaleString()}
              </span>
              {homeEquiv && (
                <span className="mt-0.5 text-xs text-text-secondary">
                  {homeEquiv}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Fee Section */}
      <div className="mx-4 mt-5 rounded-lg bg-gray-100 p-4">
        <div className="flex items-center justify-between">
          <span className="font-body text-sm text-text-secondary">
            {t('charge.fee')}: &yen;{FEE_CONFIG.charge.fixed} + {(FEE_CONFIG.charge.rate * 100).toFixed(1)}%
          </span>
          {feeInfo && (
            <span className="font-body text-sm font-medium text-text-primary">
              &yen;{feeInfo.fee.toLocaleString()}
            </span>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <span className="font-body text-sm font-semibold text-text-primary">
            {t('charge.total')}
          </span>
          <span className="font-display text-lg font-bold text-text-primary">
            &yen;{feeInfo ? feeInfo.total.toLocaleString() : '---'}
          </span>
        </div>
      </div>

      {/* Daily limit warning */}
      {exceedsLimit && (
        <div className="mx-4 mt-4 rounded-lg bg-red-50 border border-red-200 p-3">
          <p className="font-body text-sm text-red-600 font-medium">
            {t('charge.dailyLimit')}
          </p>
          <p className="font-body text-xs text-red-500 mt-1">
            {t('charge.dailyRemaining', { remaining: dailyRemaining.toLocaleString() })}
          </p>
        </div>
      )}

      {/* #1188 Phase 2: 金額制限の一般説明 (弊社日次上限 + Stripe 側の上限は
         Phase 0 #1193 で確認中の旨)。次画面で method 別の詳細も出る。 */}
      <div className="mx-4 mt-4 rounded-lg bg-gray-50 border border-gray-200 p-3">
        <p className="font-body text-xs text-text-secondary leading-5">
          {t('charge.limitNote', {
            daily: (DAILY_CHARGE_LIMIT / 10000).toString(),
          })}
        </p>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* CTA Button */}
      <div className="px-4 pb-6 pt-4">
        <button
          onClick={handleContinue}
          disabled={selected === null || exceedsLimit}
          className={`tap-effect h-14 w-full rounded-xl font-display text-base font-semibold text-white transition-colors ${
            selected !== null && !exceedsLimit
              ? 'bg-yokoso-red shadow-button hover:bg-yokoso-red-dark'
              : 'cursor-not-allowed bg-gray-300'
          }`}
        >
          {t('charge.continue')}
        </button>
      </div>
    </div>
  )
}
